import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { useAnswerSurvey, usePublicSurvey } from './surveyQueries';

const RATINGS = [1, 2, 3, 4, 5];

/**
 * Die Zufriedenheitsumfrage -- F5-10, K-150.
 *
 * **Ohne Anmeldung, ueber ein Merkmal im Link.** Der Befragte ist ein Kunde,
 * der ein Ticket gemeldet hat; ihn fuer eine Bewertung erst ein Kennwort
 * suchen zu lassen hiesse, keine Antworten zu bekommen. Die Absicherung liegt
 * im Merkmal: Es oeffnet genau eine Umfrage, gilt einmal, und die Policy in
 * Migration 0027 laesst nichts anderes durch.
 *
 * **Diese Seite liegt im Portalbuendel und nicht in der Agentenoberflaeche.**
 * Sie wird an jeden ausgeliefert, der einen Link hat -- und im Agentencode
 * stehen Feldnamen, Endpunkte und Bedienlogik, die einen Kunden nichts
 * angehen (docs/02, Abschnitt 4).
 *
 * Sie zeigt Ticketnummer und Betreff und sonst nichts: Der Link steht in
 * einer Mail, und Mails werden weitergeleitet.
 */
export default function SurveyPage() {
  const { t } = useTranslation();
  // `strict: false` statt eines Verweises auf die Route: Diese Datei wird
  // nachgeladen und darf `app/router.tsx` nicht importieren -- das zoege den
  // gesamten Routenbaum samt Agentenoberflaeche in das Portalbuendel.
  // `strict: false` liefert den Wert als moeglicherweise fehlend -- die
  // Route legt ihn fest, aber der Typ weiss das ohne Routenbezug nicht. Ein
  // leeres Merkmal fuehrt zum selben 404 wie ein falsches; das ist richtig so.
  const { token = '' } = useParams({ strict: false });
  const survey = usePublicSurvey(token);
  const answer = useAnswerSurvey(token);

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const done = answer.isSuccess || survey.data?.answered === true;

  return (
    <div className="centered-page">
      <div className="card centered-page__card">
        <h1>{t('survey.title')}</h1>

        {survey.isPending ? <LoadingIndicator /> : null}
        {survey.isError ? <ErrorNotice error={survey.error} /> : null}

        {survey.data ? (
          <p className="table__muted">
            {t('survey.subtitle', {
              number: survey.data.ticket_number,
              title: survey.data.ticket_title,
            })}
          </p>
        ) : null}

        {done ? (
          <div className="notice notice--success" role="status">
            <p>{t('survey.thanks')}</p>
          </div>
        ) : null}

        {survey.data && !done ? (
          <>
            <div className="field">
              <span className="field__label">{t('survey.rating')}</span>
              <div className="row" role="group" aria-label={t('survey.rating')}>
                {RATINGS.map((value) => (
                  <Button
                    key={value}
                    variant={rating === value ? 'primary' : 'ghost'}
                    aria-pressed={rating === value}
                    aria-label={t('survey.rateWith', { value })}
                    onClick={() => {
                      setRating(value);
                    }}
                  >
                    {String(value)}
                  </Button>
                ))}
              </div>
              <span className="field__hint">{t('survey.ratingHint')}</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="survey-comment">
                {t('survey.comment')}
              </label>
              <textarea
                id="survey-comment"
                className="field__control"
                rows={4}
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
            </div>

            {answer.isError ? <ErrorNotice error={answer.error} /> : null}

            <Button
              variant="primary"
              block
              disabled={rating === null}
              busy={answer.isPending}
              onClick={() => {
                if (rating !== null) answer.mutate({ rating, comment });
              }}
            >
              {t('survey.submit')}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
